import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SiteCard from '@/components/feature/SiteCard';
import usePageMeta from '@/hooks/usePageMeta';
import { navSites, siteCategories } from '@/mocks/navigation';

export default function Category() {
  const { slug } = useParams();
  const category = siteCategories.find((item) => item.id === slug);
  const categorySites = useMemo(
    () => navSites.filter((site) => site.category === slug).sort((a, b) => b.heat - a.heat),
    [slug],
  );

  const options = useMemo(() => {
    const base = (typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '/').replace(/\/$/, '');
    const url = `${window.location.origin}${base}/category/${slug}`;
    const name = category?.name ?? '分类';
    return {
      title: `${name}导航 - ${name}网站目录与资源整理 | 爆料导航网`,
      description:
        category?.intro.slice(0, 70) ??
        '爆料导航网分类页，按领域整理同类信息站点，方便顺藤摸瓜找到更多入口。',
      keywords: `${name},${name}导航,${name}网站目录,爆料导航分类,爆料导航资源整理`,
      path: `/category/${slug}`,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'CollectionPage',
            name: `${name}导航目录`,
            url,
            inLanguage: 'zh-CN',
            description: category?.desc,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}${base}/` },
              { '@type': 'ListItem', position: 2, name: '分类导航', item: `${window.location.origin}${base}/#categories` },
              { '@type': 'ListItem', position: 3, name, item: url },
            ],
          },
          {
            '@type': 'ItemList',
            name: `${name}站点列表`,
            numberOfItems: categorySites.length,
            itemListElement: categorySites.map((site, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: site.name,
              url: site.url,
            })),
          },
        ],
      },
    };
  }, [category, categorySites, slug]);

  usePageMeta(options);

  const hotTags = Array.from(new Set(categorySites.flatMap((site) => site.tags))).slice(0, 10);
  const relatedCategories = siteCategories.filter((item) => item.id !== slug).slice(0, 4);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-8 bg-background-100">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: '分类导航', to: '/#categories' },
              { label: category?.name ?? '分类' },
            ]}
          />

          {category ? (
            <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-2xl">
                    <i className={category.icon} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                      {category.name} 导航目录
                    </h1>
                    <p className="mt-1 font-label text-xs text-secondary-700">{category.tagline}</p>
                  </div>
                </div>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">{category.intro}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-4 shrink-0">
                <p className="font-label text-xs text-foreground-500">本分类收录</p>
                <p className="mt-1 font-heading text-2xl font-bold text-primary-600">
                  {categorySites.length} 个站点
                </p>
                <p className="mt-1 font-label text-[11px] text-foreground-500">每周复核一次</p>
              </div>
            </div>
          ) : null}
        </section>

        {category ? (
          <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <aside className="lg:col-span-3 order-2 lg:order-1">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <nav aria-label="分类导航" className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <h2 className="font-heading text-sm font-bold text-foreground-950">全部分类</h2>
                    <ul className="mt-3 space-y-1">
                      {siteCategories.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/category/${item.id}`}
                            className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-2 font-label text-sm transition-colors ${
                              item.id === slug
                                ? 'bg-primary-500 text-background-50'
                                : 'text-foreground-700 hover:bg-background-200'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <i className={item.icon} aria-hidden="true"></i>
                              {item.name}
                            </span>
                            <span className="text-[11px] opacity-80">
                              {navSites.filter((site) => site.category === item.id).length}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>

                  <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <h2 className="font-heading text-sm font-bold text-foreground-950">本分类热门标签</h2>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {hotTags.map((tag) => (
                        <span
                          key={tag}
                          className="font-label text-[11px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
                    <h2 className="font-heading text-sm font-bold text-primary-900">发现遗漏的站点</h2>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                      如果你常用某个 {category.name} 方向的站点还没被收录，欢迎提交给我们。
                    </p>
                    <Link
                      to="/submit"
                      className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 font-label text-xs whitespace-nowrap hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-add-circle-line" aria-hidden="true"></i>
                      提交收录
                    </Link>
                  </div>
                </div>
              </aside>

              <div className="lg:col-span-9 order-1 lg:order-2">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                    收录站点列表
                  </h2>
                  <p className="font-label text-xs text-foreground-500">按热度排序</p>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {categorySites.map((site) => (
                    <SiteCard key={site.id} site={site} categoryName={category.name} />
                  ))}
                </div>

                <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950">继续逛其他分类</h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    同类站点看完了？这几个方向也常常有值得一读的内容。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {relatedCategories.map((item) => (
                      <Link
                        key={item.id}
                        to={`/category/${item.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-3.5 py-1.5 font-label text-xs text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors"
                      >
                        <i className={item.icon} aria-hidden="true"></i>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="w-full px-4 md:px-6 lg:px-10 py-20 bg-background-50">
            <div className="flex flex-col items-center text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
                <i className="ri-compass-3-line" aria-hidden="true"></i>
              </span>
              <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
              <p className="mt-3 max-w-md text-sm text-foreground-600">
                该分类可能已经调整或合并。你可以回到首页查看全部十个分类方向。
              </p>
              <Link
                to="/#categories"
                className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-label text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                返回分类导航
              </Link>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}